import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from '@fluentui/react-components';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { useTranslation } from 'react-i18next';

import { useStorageDialogsStyles } from './StorageDialogs.Styles';
import { validateFolderName } from '../../../functions/StorageManagement.Functions';
import { ErrorType } from '../../../models/HttpError';

export interface FolderNameDialogProps {
  open: boolean;
  mode: 'create' | 'rename';
  /** Rename: current name of the folder */
  initialName?: string;
  /** Friendly name of the parent folder (create) or of the folder (rename) for the description */
  folderLabel?: string;
  /** Folders already in the parent (duplicate check) */
  siblingNames: string[];
  /**
   * Creates / renames. Resolves with a success message to keep the dialog open showing it (rename result),
   * or with nothing to close it. Rejects with ErrorType.
   */
  onSubmit: (name: string) => Promise<string | void>;
  onDismiss: () => void;
  /** Error → text (409 / 400 / partial failure messages) */
  describeError: (error: ErrorType) => string;
}

/** "Nueva carpeta" / "Renombrar carpeta": name with the §9.1 rules validated inline. */
export const FolderNameDialog = (props: FolderNameDialogProps) => {
  const { t } = useTranslation();
  const styles = useStorageDialogsStyles();
  const { open, mode, initialName } = props;

  const [name, setName] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<ErrorType | undefined>(undefined);
  const [success, setSuccess] = React.useState<string | undefined>(undefined);
  const mounted = React.useRef(true);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (open) {
      setName(initialName ?? '');
      setTouched(false);
      setSaving(false);
      setError(undefined);
      setSuccess(undefined);
    }
  }, [open, initialName]);

  const validation = validateFolderName(name, props.siblingNames, mode === 'rename' ? initialName : undefined);
  const showValidation = touched && !!validation;

  const submit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setTouched(true);
    if (validation || saving) return;
    setSaving(true);
    setError(undefined);
    try {
      const message = await props.onSubmit(name);
      if (!mounted.current) return;
      setSaving(false);
      if (message) setSuccess(message);
      else props.onDismiss();
    } catch (failure: unknown) {
      if (!mounted.current) return;
      setSaving(false);
      setError(failure as ErrorType);
    }
  };

  const isRename = mode === 'rename';
  const errorDetails = (error?.Errors ?? []).filter(Boolean);

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open && !saving) props.onDismiss();
      }}
    >
      <DialogSurface aria-describedby={undefined}>
        <form onSubmit={submit} noValidate>
          <DialogBody>
            <DialogTitle>{t(isRename ? 'filesExplorer.folderDialog.renameTitle' : 'filesExplorer.folderDialog.createTitle')}</DialogTitle>
            <DialogContent className={styles.content}>
              {props.folderLabel && (
                <p className={styles.secondaryText}>
                  {t(isRename ? 'filesExplorer.folderDialog.renameHelp' : 'filesExplorer.folderDialog.createHelp', {
                    folder: props.folderLabel,
                  })}
                </p>
              )}
              {isRename && !success && (
                <GuideMessageBar intent="warning" layout="multiline" data-testid="folder-rename-warning">
                  <MessageBarBody>
                    <MessageBarTitle>{t('filesExplorer.folderDialog.renameWarningTitle')}</MessageBarTitle>
                    {t('filesExplorer.folderDialog.renameWarning')}
                  </MessageBarBody>
                </GuideMessageBar>
              )}
              {success ? (
                <GuideMessageBar intent="success" layout="multiline" data-testid="folder-dialog-success">
                  <MessageBarBody>{success}</MessageBarBody>
                </GuideMessageBar>
              ) : (
                <Field
                  label={t('filesExplorer.folderDialog.nameLabel')}
                  required
                  validationState={showValidation ? 'error' : 'none'}
                  validationMessage={showValidation && validation ? t(validation) : undefined}
                  hint={!showValidation ? t('filesExplorer.folderDialog.nameHint') : undefined}
                >
                  <Input
                    value={name}
                    onChange={(_, data) => {
                      setName(data.value);
                      setTouched(true);
                      setError(undefined);
                    }}
                    disabled={saving}
                    maxLength={120}
                    autoFocus
                    data-testid="folder-name-input"
                  />
                </Field>
              )}
              {error && (
                <GuideMessageBar intent="error" layout="multiline" data-testid="folder-dialog-error">
                  <MessageBarBody>
                    <MessageBarTitle>
                      {t(isRename ? 'filesExplorer.folderDialog.renameErrorTitle' : 'filesExplorer.folderDialog.createErrorTitle')}
                    </MessageBarTitle>
                    {props.describeError(error)}
                    {errorDetails.length > 1 && (
                      <ul className={styles.errorList} data-testid="folder-dialog-error-list">
                        {errorDetails.map((detail, index) => (
                          <li key={index}>{detail}</li>
                        ))}
                      </ul>
                    )}
                  </MessageBarBody>
                </GuideMessageBar>
              )}
            </DialogContent>
            <DialogActions>
              {success ? (
                <Button appearance="primary" onClick={props.onDismiss} data-testid="folder-dialog-close">
                  {t('filesExplorer.actions.close')}
                </Button>
              ) : (
                <>
                  <Button appearance="secondary" onClick={props.onDismiss} disabled={saving}>
                    {t('filesExplorer.actions.cancel')}
                  </Button>
                  <Button
                    appearance="primary"
                    type="submit"
                    disabled={saving || (touched && !!validation)}
                    icon={saving ? <Spinner size="extra-tiny" /> : undefined}
                    data-testid="folder-dialog-submit"
                  >
                    {t(isRename ? 'filesExplorer.actions.rename' : 'filesExplorer.actions.create')}
                  </Button>
                </>
              )}
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};

export default FolderNameDialog;
